import {
  getRequestMetadata,
  recordRequestMetadata,
} from "~/error-handler/requestMetadata";

type ApiFetchOptions = {
  /**
   * A stable label for the request, used to group failures in telemetry. URLs
   * usually contain ids, so they cannot serve this purpose.
   */
  operation: string;
};

/**
 * The request never produced a response: the browser was offline, the
 * connection dropped, or the request was aborted.
 */
export class NetworkError extends Error {
  readonly operation: string;
  readonly url: string;
  readonly cause: unknown;

  constructor(operation: string, url: string, cause: unknown) {
    super(cause instanceof Error ? cause.message : String(cause));
    this.name = "NetworkError";
    this.operation = operation;
    this.url = url;
    this.cause = cause;
  }
}

/** The server responded with a non-2xx status. */
export class HttpError extends Error {
  readonly status: number;
  readonly operation: string;
  readonly url: string;
  readonly sentToken: string | null;
  /**
   * The stack of the code that made the request. This error's own stack only
   * reaches back to the last `await`, so it cannot say which caller failed.
   */
  readonly callStack?: string;

  constructor(
    message: string,
    status: number,
    operation: string,
    url: string,
    sentToken: string | null,
    callStack?: string
  ) {
    super(message);
    this.name = "HttpError";
    this.status = status;
    this.operation = operation;
    this.url = url;
    this.sentToken = sentToken;
    this.callStack = callStack;
  }

  static fromResponse(response: Response, message?: string): HttpError {
    const metadata = getRequestMetadata(response);
    const operation = metadata?.operation ?? "unknown";

    return new HttpError(
      message ?? `HTTP ${response.status} · ${operation}`,
      response.status,
      operation,
      metadata?.url ?? response.url,
      metadata?.sentToken ?? null,
      metadata?.callStack
    );
  }
}

function bearerToken(init: RequestInit): string | null {
  const authorization = new Headers(init.headers).get("Authorization") ?? "";
  const token = authorization.replace(/^Bearer\s*/i, "");
  return token.length > 0 ? token : null;
}

/**
 * `fetch` for API requests. A rejected request becomes a typed `NetworkError`;
 * every response, OK or not, is returned unchanged so callers keep their own
 * status handling. Use `HttpError.fromResponse` to turn a non-OK response into
 * an error that carries its status for classification.
 */
export async function apiFetch(
  url: string,
  init: RequestInit,
  { operation }: ApiFetchOptions
): Promise<Response> {
  // Captured before the first await, while the caller is still on the stack.
  const callStack = new Error().stack;

  let response: Response;
  try {
    response = await window.fetch(url, init);
  } catch (err) {
    throw new NetworkError(operation, url, err);
  }

  recordRequestMetadata(response, {
    operation,
    url,
    sentToken: bearerToken(init),
    callStack,
  });

  return response;
}
